import React from 'react'
import moment from 'moment'
import { sv } from 'date-fns/locale'
import Table from '@/components/table/Table'
import Row from '@/components/table/Row'
import Switch from '@mui/material/Switch'
import Grid from '@mui/material/Grid'
import Skeleton from '@mui/material/Skeleton'
import { getWindowDimensions, windowsWidth } from '@/utils/screenUtils'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ActionButton from '@/components/buttons/ActionButton'
import Button from '@mui/material/Button'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useLazyGetSchemaQuery } from '@/store/apis/schemas'
import {
  useLazyAddAttendanceQuery,
  useLazyGetAttendancesQuery,
  useLazyRemoveAttendanceQuery,
  ITimeslot,
} from '@/store/apis/timeslots'

interface IUserAttendance {
  id: number
  attendanceId?: number
  firstName: string
  lastName?: string
}
interface IAttendacesWithPendingUser {
  timeslot: ITimeslot
  name: string
  users: IUserAttendance[]
}

let intervalId: NodeJS.Timeout | undefined

const Attendance: React.FC = () => {
  const { t } = useTranslation()
  const urlParams = useParams()
  const [getSchemaQuery, { data: schemaData, isLoading: isLoadingSchemaData }] =
    useLazyGetSchemaQuery()
  const [addAttendance, { isFetching: isAddingAttendance }] =
    useLazyAddAttendanceQuery()
  const [
    getAttendances,
    { data: attendancesData, isLoading: isLoadingAttendances },
  ] = useLazyGetAttendancesQuery()
  const [removeAttendance, { isFetching: isRemovingAttendance }] =
    useLazyRemoveAttendanceQuery()
  const [selectedDay, setSelectedDay] = React.useState<
    moment.Moment | undefined
  >(moment(new Date()))
  const [attendances, setAttendances] = React.useState<
    IAttendacesWithPendingUser[]
  >([])
  const schemaId = Number(urlParams.schemaId)
  const hasRun = React.useRef(false)

  const [windowDimensions, setWindowDimensions] = React.useState(
    getWindowDimensions(),
  )
  React.useEffect(() => {
    const handleResize = () => {
      setWindowDimensions(getWindowDimensions())
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])
  const isMobileWidth = windowDimensions.width < windowsWidth

  const handletGetAttandances = (day: string) => {
    intervalId ? clearInterval(intervalId) : undefined
    const newIntervalId = setInterval(() => {
      getAttendances({ schemaId, day })
    }, 5000) // 5 sek
    intervalId = newIntervalId
  }

  React.useEffect(() => {
    hasRun.current = true
    getSchemaQuery({ id: schemaId })
    if (selectedDay && !attendancesData && !schemaData) {
      getAttendances({ schemaId, day: selectedDay.toISOString() })
      handletGetAttandances(selectedDay.toISOString())
    }
    return () => {
      clearInterval(intervalId)
    }
  }, [hasRun.current])

  React.useEffect(() => {
    if (schemaData && attendancesData) {
      const newAttendances = attendancesData.items.map((attendance) => {
        const sceneIndex = schemaData.scenes.findIndex(
          (scene) =>
            scene.timeslots.findIndex(
              (timeslot) => timeslot.id === attendance.timeslot.id,
            ) > -1,
        )
        const sceneGroupIndex = schemaData.sceneGroups.findIndex(
          (sceneGroup) =>
            sceneGroup.timeslots.findIndex(
              (timeslot) => timeslot.id === attendance.timeslot.id,
            ) > -1,
        )
        if (sceneIndex > -1) {
          const attendedUsers = attendance.attendances.map((att) => {
            return {
              id: att.user.id,
              attendanceId: att.id,
              firstName: att.user.firstName,
              lastName: att.user.lastName,
            }
          }) as IUserAttendance[]
          const notAttendedUsers = schemaData.scenes[sceneIndex].users.reduce(
            (list, sceneUser) => {
              if (
                attendance.attendances.findIndex(
                  (att) => att.user.id === sceneUser.id,
                ) === -1
              ) {
                const schemaUser = schemaData.users.find(
                  (schemaUser) => schemaUser.id === sceneUser.id,
                )
                list.push({
                  id: schemaUser.id,
                  firstName: schemaUser.firstName,
                  lastName: schemaUser.lastName,
                })
              }
              return list
            },
            [] as IUserAttendance[],
          )
          let mergedList = attendedUsers.concat(notAttendedUsers)
          mergedList = mergedList.sort(
            (a: IUserAttendance, b: IUserAttendance) =>
              `${a.firstName} ${a.lastName}` < `${b.firstName} ${b.lastName}`
                ? -1
                : 1,
          )
          return {
            timeslot: attendance.timeslot,
            name: attendance.name,
            users: mergedList,
          }
        } else if (sceneGroupIndex > -1) {
          const attendedUsers = attendance.attendances.map((att) => {
            return {
              id: att.user.id,
              attendanceId: att.id,
              firstName: att.user.firstName,
              lastName: att.user.lastName,
            }
          }) as IUserAttendance[]
          const notAttendedUsers = schemaData.sceneGroups[
            sceneGroupIndex
          ].scenes.reduce((list, sceneId) => {
            const scenes = schemaData.scenes.filter(
              (scene) => scene.scene.id === sceneId.id,
            )
            scenes.map((scene) => {
              scene.users.filter((sceneUser) => {
                const foundIndex = list.findIndex(
                  (listUser) => listUser.id === sceneUser.id,
                )
                if (foundIndex === -1) {
                  const schemaUser = schemaData.users.find(
                    (schemaUser) => schemaUser.id === sceneUser.id,
                  )
                  list.push({
                    id: schemaUser.id,
                    firstName: schemaUser.firstName,
                    lastName: schemaUser.lastName,
                  })
                }
              })
            })
            return list
          }, [] as IUserAttendance[])
          let mergedList = attendedUsers.concat(notAttendedUsers)
          mergedList = mergedList.sort(
            (a: IUserAttendance, b: IUserAttendance) =>
              `${a.firstName} ${a.lastName}` < `${b.firstName} ${b.lastName}`
                ? -1
                : 1,
          )
          return {
            timeslot: attendance.timeslot,
            name: attendance.name,
            users: mergedList,
          }
        }
        return {
          timeslot: attendance.timeslot,
          name: attendance.name,
          users: [],
        }
      })
      setAttendances(newAttendances)
    }
  }, [schemaData, attendancesData])

  const handleAddAttendance = async (timeslotId: number, userId: number) => {
    await addAttendance({ schemaId, userId, timeslotId })
    getAttendances({ schemaId, day: selectedDay.toISOString() })
  }

  const handleRemoveAttendance = async (attendanceId: number) => {
    await removeAttendance({ schemaId, id: attendanceId })
    getAttendances({ schemaId, day: selectedDay.toISOString() })
  }

  const handleChangeDay = async (newDay: moment.Moment) => {
    setSelectedDay(newDay)
    getAttendances({ schemaId, day: newDay.toISOString() })
    handletGetAttandances(newDay.toISOString())
  }

  return (
    <>
      <Grid
        container
        columnSpacing={2}
        alignItems="center"
        justifyContent="center"
      >
        <Grid item xs>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={sv}>
            <DateCalendar
              sx={{
                height: '300px',
              }}
              disablePast
              displayWeekNumber
              showDaysOutsideCurrentMonth
              value={selectedDay ? selectedDay.toDate() : new Date()}
              minDate={
                schemaData
                  ? new Date(schemaData.schema.schema.startDate)
                  : new Date()
              }
              maxDate={
                schemaData
                  ? new Date(schemaData.schema.schema.endDate)
                  : new Date()
              }
              onChange={(value) =>
                handleChangeDay(moment(value ? (value as Date) : new Date()))
              }
            />
          </LocalizationProvider>
        </Grid>
      </Grid>
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="flex-end"
        marginTop={2}
        marginBottom={2}
      >
        <Grid item xs textAlign="left">
          <ActionButton
            label={!isMobileWidth && t('buttons.previusDay')}
            icon={<ChevronLeftIcon />}
            disabled={selectedDay.isSameOrBefore(
              moment(schemaData?.schema.schema.startDate),
              'day',
            )}
            onClick={() =>
              handleChangeDay(moment(selectedDay).subtract(1, 'day'))
            }
          />
        </Grid>
        <Grid item xs textAlign="center">
          <Button
            size="small"
            variant="contained"
            onClick={() => handleChangeDay(moment(new Date()))}
          >
            {t('buttons.today')}
          </Button>
        </Grid>
        <Grid item xs textAlign="right">
          <ActionButton
            label={!isMobileWidth && t('buttons.nextDay')}
            icon={<ChevronRightIcon />}
            endIcon={true}
            disabled={selectedDay.isSameOrAfter(
              moment(schemaData?.schema.schema.endDate),
              'day',
            )}
            onClick={() => handleChangeDay(moment(selectedDay).add(1, 'day'))}
          />
        </Grid>
      </Grid>
      {!isLoadingAttendances && !attendancesData?.items.length ? (
        <>
          <div
            style={{
              height: '2em',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <b>
              <i>{t('noRehearsal')}</i>
            </b>
          </div>
        </>
      ) : !isLoadingAttendances ? (
        <>
          {attendances.map((attendance, index) => (
            <React.Fragment key={index}>
              <Grid
                container
                columnSpacing={2}
                alignItems="center"
                justifyContent="flex-start"
                flexDirection="column"
              >
                <Grid item xs>
                  <b>
                    {moment(
                      new Date(attendance.timeslot.startTimestamp),
                    ).format('HH:mm')}{' '}
                    -{' '}
                    {moment(new Date(attendance.timeslot.endTimestamp)).format(
                      'HH:mm',
                    )}
                  </b>
                </Grid>
                <Grid item xs>
                  {attendance.name}
                </Grid>
                <Grid item xs>
                  <i>{attendance.timeslot.comment}</i>
                </Grid>
              </Grid>
              <Table
                headerLabels={[
                  t('headers.firstName'),
                  t('headers.sureName'),
                  t('headers.attendanceNoAttandance'),
                ]}
                isLoading={isLoadingSchemaData || isLoadingAttendances}
                noButtons
              >
                {attendance.users.map((user, index) => (
                  <Row
                    key={index}
                    columns={{
                      normal: [
                        user.firstName,
                        user.lastName,
                        <Switch
                          color="primary"
                          disabled={isAddingAttendance || isRemovingAttendance}
                          checked={user.attendanceId !== undefined}
                          onClick={(event) => {
                            event.stopPropagation()
                            if (user.attendanceId === undefined) {
                              handleAddAttendance(
                                attendance.timeslot.id,
                                user.id,
                              )
                            } else {
                              handleRemoveAttendance(user.attendanceId)
                            }
                          }}
                        />,
                      ],
                    }}
                  />
                ))}
              </Table>
            </React.Fragment>
          ))}
        </>
      ) : (
        <>
          <Grid
            container
            columnSpacing={2}
            alignItems="center"
            justifyContent="flex-start"
            flexDirection="column"
          >
            <Grid item xs>
              <Skeleton style={{ width: '100px' }} />
            </Grid>
            <Grid item xs>
              <Skeleton style={{ width: '100px' }} />
            </Grid>
          </Grid>
          <Table
            headerLabels={[
              t('headers.firstName'),
              t('headers.sureName'),
              t('headers.attendanceNoAttandance'),
            ]}
            isLoading={true}
            noButtons
          >
            {new Array(10).fill(0).map((_notused, index) => (
              <Row
                key={index}
                columns={{
                  normal: ['', '', ''],
                }}
              />
            ))}
          </Table>
        </>
      )}
    </>
  )
}

export default Attendance
